import { useEffect, useRef, useState } from "react";
import { clearDraft, readDraft, writeDraft } from "@/lib/ytreDraftCache";

/**
 * Keeps a card's half-typed edit alive across unmounts (Sep 30, 2026).
 *
 * Returns the editing plumbing plus `recovered` — set only when a stored draft
 * actually differs from what the server now holds, so a card whose edit was
 * saved (or overtaken by a redraft) never nags.
 */
export function useCardDraft({ id, baseline, field, onStart }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [recovered, setRecovered] = useState(null);
  const restoredRef = useRef(false);

  // Restore once per mount, and only if it is still a real divergence.
  useEffect(() => {
    if (restoredRef.current || !id) return;
    restoredRef.current = true;
    const found = readDraft(id);
    if (!found) return;
    if (found.text === (baseline || "")) { clearDraft(id); return; }
    setDraft(found.text);
    setRecovered(found);
    setEditing(true);
    onStart?.();
  }, [id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror keystrokes (debounced). Typing back to the saved text clears it.
  useEffect(() => {
    if (!editing || !id) return;
    if (draft === (baseline || "")) { clearDraft(id); return; }
    const t = setTimeout(() => writeDraft(id, draft, field), 400);
    return () => clearTimeout(t);
  }, [draft, editing, id, baseline, field]);

  const start = () => {
    setDraft(baseline || "");
    setRecovered(null);
    setEditing(true);
    onStart?.();
  };
  const discard = () => {
    clearDraft(id);
    setRecovered(null);
    setEditing(false);
  };
  const commit = async (save) => {
    await save(draft);
    clearDraft(id);
    setRecovered(null);
    setEditing(false);
  };
  const revertToSaved = () => {
    clearDraft(id);
    setRecovered(null);
    setDraft(baseline || "");
  };

  return { editing, setEditing, draft, setDraft, recovered, start, discard, commit, revertToSaved };
}
